/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { Input, message } from 'antd';
import md5 from 'md5';
import { SubmitButton } from './styles';
import { InputLabel, InputContainer } from '../../components/Modals/styles';
import TitleBanner from '../../components/TitleBanner';
import { PASSWORD_MAX_LENGTH } from '../../utils/helper';
import { preventAction } from './Tools';
import { submitNewPassword } from 'supertokens-web-js/recipe/emailpassword';
import { SET_USER_PASSWORD } from './translationValues';

const NAMESPACES = ['SET_USER_PASSWORD'];
const PasswordStrengthBar = React.lazy(() =>
  import('../../components/PasswordStrengthBar/PasswordStrengthBar'),
);

const ForgotPassword = props => {
  const { t } = props;
  const history = useHistory();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [PasswordScore, setPasswordScore] = useState('');
  const [loading, setLoading] = useState(false);

  // new and confirm password validation
  const passwordValidation = (password, confirmPassword) => {
    if (password === confirmPassword) {
      setRepeatPassword(false);
    } else {
      setRepeatPassword(true);
    }
  };

  // get score from new password
  const onChangeScore = value => {
    setPassword(value.password);
    setPasswordScore(value.score);

    if (confirmPassword.length === 0) return;
    passwordValidation(value.password, confirmPassword)
  };

  const getConfirmPassword = e => {
    const value = e.target.value;
    setConfirmPassword(value);
    passwordValidation(password, value)
  };

  // update new password
  const onSubmitForm = async () => {
    if (loading || disabled) return;

    if (repeatPassword) {
      return;
    };
    if (PasswordScore < 3) {
      message.warning(t(SET_USER_PASSWORD.CHECK_PASSWORD_MESSAGE.key, SET_USER_PASSWORD.CHECK_PASSWORD_MESSAGE.value));
      return;
    };

    setDisabled(true)
    setLoading(true);

    const resetButtonState = () => {
      setDisabled(false);
      setLoading(false);
    }

    try {
      let response = await submitNewPassword({
        formFields: [
          {
            id: 'password',
            value: md5(password),
          },
        ],
      });

      if (response.status === 'FIELD_ERROR') {
        response.formFields.forEach(formField => {
          if (formField.id === 'password') {
            // New password did not meet password criteria on the backend.
            message.error(formField.error);
          }
        });
      } else if (response.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
        // the password reset token in the URL is invalid, expired, or already consumed
        message.error(t(SET_USER_PASSWORD.PASSWORD_RESET_FAILED_ERROR_MESSAGE.key, SET_USER_PASSWORD.PASSWORD_RESET_FAILED_ERROR_MESSAGE.value));
      } else {
        // password updated  successfully!
        message.success(t(SET_USER_PASSWORD.PASSWORD_SET_MSG.key, SET_USER_PASSWORD.PASSWORD_SET_MSG.value))

        setTimeout(() => {
          history.push('/');
        }, 2500);
      }
    } catch (err) {
      resetButtonState();
      if (err.isSuperTokensGeneralError === true) {
        // this may be a custom error message sent from the API by you.
        message.error(err.message);
      } else {
        message.error(t(SET_USER_PASSWORD.GENERAL_ERROR_MESSAGE.key, SET_USER_PASSWORD.GENERAL_ERROR_MESSAGE.value));
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      !disabled && onSubmitForm();
    }
  }

  useEffect(() => {
    if (password === confirmPassword && password && confirmPassword && !loading) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [password, confirmPassword, loading]);

  return (
    <>
      <div className="right-side">
        <TitleBanner title="RESET PASSWORD" />
        <div className="content-body" onKeyPress={handleKeyDown} onPaste={preventAction}>
          <div className="bottom_top_anime">
            <InputContainer bottom-0 signUp>
              <InputLabel>{t(SET_USER_PASSWORD.SET_PASSWORD_LABEL.key, SET_USER_PASSWORD.SET_PASSWORD_LABEL.value)}</InputLabel>
              <PasswordStrengthBar onChangeScore={onChangeScore} />
            </InputContainer>

            <div className="bottom_top_anime">
              <InputContainer bottom-0 signUp>
                <InputLabel>{t(SET_USER_PASSWORD.SET_CONFIRM_PASSWORD_LABEL.key, SET_USER_PASSWORD.SET_CONFIRM_PASSWORD_LABEL.value)}</InputLabel>
                <Input.Password
                  size="large"
                  placeholder={t(SET_USER_PASSWORD.SET_CONFIRM_PASSWORD_PLACEHOLDER.key, SET_USER_PASSWORD.SET_CONFIRM_PASSWORD_PLACEHOLDER.value)}
                  value={confirmPassword}
                  onChange={e => getConfirmPassword(e)}
                  className="inputBox"
                  onPaste={preventAction}
                  onCopy={preventAction}
                  maxLength={PASSWORD_MAX_LENGTH}
                />
                {repeatPassword && (
                  <InputLabel error signUp>
                    {t(SET_USER_PASSWORD.PASSWORD_MISMATCH_ERROR_MESSAGE.key, SET_USER_PASSWORD.PASSWORD_MISMATCH_ERROR_MESSAGE.value)}
                  </InputLabel>
                )}
              </InputContainer>
            </div>
          </div>
        </div>
        <SubmitButton disable={disabled} onClick={onSubmitForm}>
          <div className="wrap-1">
            {t(SET_USER_PASSWORD.RESET_PASSWORD_BUTTON.key, SET_USER_PASSWORD.RESET_PASSWORD_BUTTON.value)}
            <div className="arrow"></div>
          </div>
          <div className="wrap-2">
            {t(SET_USER_PASSWORD.RESET_PASSWORD_BUTTON.key, SET_USER_PASSWORD.RESET_PASSWORD_BUTTON.value)}
            <div className="arrow"></div>
          </div>
        </SubmitButton>
      </div >
    </>
  );
};

export default withTranslation(NAMESPACES)(ForgotPassword);
